import type { Signal } from '@builder.io/qwik';
import type { AppDataInterface } from '~/types/AppData/AppDataInterface';

/**
 * Fetches and sets the next article to be lazily loaded based on the finite scroll index.
 *
 * @param {AppDataInterface} appData - The application data containing environmental settings and content.
 * @param {number} finiteScrollIndex - The current index used for finite scrolling.
 * @param {Signal<ArticleInterface>} nextArticle - The signal to be updated with the next article.
 * @returns {Promise<void>} - A promise that resolves when the next article is fetched and set.
 */
export const getLazyLoadArticle = async (
  appData: AppDataInterface,
  finiteScrollIndex: number,
  nextArticle: Signal
): Promise<void> => {
  const finiteScrollArticlesURLs =
    appData.payloadToSSR.pageContent.additionalPosts?.postsList.value
      .articlesQueryURLs;
  const env = appData.env;
  const template = appData.payloadToSSR.template;
  const isMobileViewer = appData.payloadToSSR.isMobileViewer;
  const newArticlesIndex = finiteScrollIndex + 1;

  if (finiteScrollArticlesURLs && finiteScrollArticlesURLs[finiteScrollIndex]) {
    try {
      const articleId = `article-${newArticlesIndex}`;
      const urlPrefix =
        env === 'development'
          ? ''
          : 'https://voltax-lightning-service.mmsport.voltaxservices.io'; // Mirror of voltax-lightning-service.mmsport.minutesvc.com with better caching
      const url = `${urlPrefix}/finiteScroll?sourceUrl=https:${encodeURIComponent(finiteScrollArticlesURLs[finiteScrollIndex])}&template=${template}&isMobileViewer=${isMobileViewer}&id=${articleId}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch article: ${response.statusText}`);
      }

      try {
        const data = await response.json();
        if (data?.data?.articles?.length) {
          nextArticle.value = data.data.articles[0];
        } else {
          throw new Error('No articles found in the response');
        }
      } catch (jsonError) {
        console.error('Error parsing JSON:', jsonError);
      }
    } catch (fetchError) {
      console.error('Error fetching article:', fetchError);
    }
  }
};
